import React, { useEffect, useState } from 'react';
import { FiUser, FiUsers } from "react-icons/fi";
import { getCigsidaCommitteeMembers } from '../../api/cigsidaApi';
import '../../styles/GSIDA.css';

const isCommitteeRole = (value) => value === true || value === 1 || value === '1';

const COMMITTEE_CATEGORIES = [
  { id: 'scientific', label: 'Comité scientifique', field: 'is_scientific' },
  { id: 'honorary', label: 'Comité d’honneur', field: 'is_honorary' },
  { id: 'experts', label: 'Experts internationaux SSR', field: 'is_expert' },
  { id: 'president', label: 'Président du Congrès', field: 'is_president' },
  { id: 'steering', label: 'Comité de pilotage', field: 'is_organizing' },
];

const getCommitteeRoles = (member) => {
  const roles = COMMITTEE_CATEGORIES
    .filter((category) => isCommitteeRole(member[category.field]))
    .map((category) => category.label);

  return roles.length ? roles : ['Comité CIGSIDA'];
};

function ExpandableText({ text, className = '' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const limit = 120;
  const isLong = text.length > limit;
  const visibleText = isLong && !isExpanded
    ? `${text.slice(0, limit).trimEnd()}…`
    : text;

  return (
    <span className={className}>
      {visibleText}
      {isLong && (
        <>
          {' '}
          <button
            type="button"
            className="speaker-read-more"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? 'Voir moins' : 'Voir plus'}
          </button>
        </>
      )}
    </span>
  );
}

export default function GSIDASpeakers() {
  const [members, setMembers] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    const loadMembers = async () => {
      setIsLoading(true);
      setError('');

      try {
        const committeeMembers = await getCigsidaCommitteeMembers();
        if (isCurrent) setMembers(committeeMembers);
      } catch (requestError) {
        console.error('Unable to load CIGSIDA committee members:', requestError);
        if (isCurrent) setError('Impossible de charger les membres du comité CIGSIDA.');
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadMembers();
    return () => {
      isCurrent = false;
    };
  }, [reloadCount]);

  const filteredMembers = activeCategory === 'all'
    ? members
    : members.filter((member) => {
      if (activeCategory === 'other') {
        return getCommitteeRoles(member)[0] === 'Comité CIGSIDA';
      }
      const category = COMMITTEE_CATEGORIES.find((item) => item.id === activeCategory);
      return category && isCommitteeRole(member[category.field]);
    });

  return (
    <div className="gsida-speakers-page">
      <div className="gsida-header-banner speakers-banner">
         <div className="banner-overlay">
            <span className="banner-tag">CONGRÈS INTERNATIONAL</span>
            <h1>Comité CIGSIDA</h1>
            <p>Découvrez les membres des comités scientifique, d’honneur, de pilotage et les experts internationaux.</p>
         </div>
      </div>

      <div className="gsida-content-container">
        <div className="speakers-grid-header">
          <FiUsers aria-hidden="true" />
          <span>Membres du Comité</span>
        </div>

        {!isLoading && !error && (
          <div className="speaker-category-tabs" aria-label="Catégories du comité">
            <button
              type="button"
              className={activeCategory === 'all' ? 'active' : ''}
              aria-pressed={activeCategory === 'all'}
              onClick={() => setActiveCategory('all')}
            >
              Tous ({members.length})
            </button>
            {COMMITTEE_CATEGORIES.map((category) => {
              const count = members.filter((member) => isCommitteeRole(member[category.field])).length;
              return (
                <button
                  type="button"
                  key={category.id}
                  className={activeCategory === category.id ? 'active' : ''}
                  aria-pressed={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.label} ({count})
                </button>
              );
            })}
            {members.some((member) => getCommitteeRoles(member)[0] === 'Comité CIGSIDA') && (
              <button
                type="button"
                className={activeCategory === 'other' ? 'active' : ''}
                aria-pressed={activeCategory === 'other'}
                onClick={() => setActiveCategory('other')}
              >
                Autres membres
              </button>
            )}
          </div>
        )}

        <div className="speakers-grid">
          {isLoading && <p className="speakers-status">Chargement des membres du comité...</p>}
          {!isLoading && error && (
            <div className="speakers-status speakers-error" role="alert">
              <p>{error}</p>
              <button type="button" onClick={() => setReloadCount((count) => count + 1)}>
                Réessayer
              </button>
            </div>
          )}
          {!isLoading && !error && members.length === 0 && (
            <p className="speakers-status">Aucun membre du comité n’est disponible pour le moment.</p>
          )}
          {!isLoading && !error && filteredMembers.length === 0 && members.length > 0 && (
            <p className="speakers-status">Aucun membre dans cette catégorie.</p>
          )}
          {!isLoading && !error && filteredMembers.map((member) => (
            <article className="speaker-card" key={member.id}>
              <div className="speaker-media">
                {member.image_url ? (
                  <img src={member.image_url} alt={member.name || 'Membre du comité CIGSIDA'} />
                ) : (
                  <div className="speaker-image-placeholder" aria-hidden="true">
                    <FiUser size={48} />
                  </div>
                )}
                {member.company_logo_url && (
                  <div className="speaker-company-logo">
                    <img src={member.company_logo_url} alt={`${member.organization || 'Organisation'} logo`} />
                  </div>
                )}
              </div>
              <div className="speaker-info">
                <h4>{member.name}</h4>
                {member.title && (
                  <p className="speaker-title-v2">
                    <ExpandableText text={member.title} />
                  </p>
                )}
                <div className="speaker-committee-tags">
                  {getCommitteeRoles(member).map((role) => (
                    <span className="speaker-committee-tag" key={role}>{role}</span>
                  ))}
                </div>

                {member.organization && (
                  <div className="speaker-meta-blueprint">
                    <ExpandableText text={member.organization} className="company-text" />
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
